import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal, EditIcon, Trash2Icon, LinkIcon, FilesIcon, EyeIcon } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import axiosInstance from '../../api/axiosInstance'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog'

import ResponsesDialog from './ResponsesDialog'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export default function DataTableRowActions({ formId, refreshData }) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: (idToDelete) =>
      axiosInstance.post('/deleteForm', {
        formId: idToDelete,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['forms'],
      })
      refreshData()
      toast.success('Form deleted successfully')
      setOpen(false)
    },

    onError: (error) => {
      const msg = error?.response?.data?.message || 'Error deleting form'
      toast.error(msg)
    },
  })

  return (
    <div className="flex items-center gap-0">
      {/* View / Open Form */}
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation()
          navigate(`/business/forms/${formId}`)
        }}
      >
        <EyeIcon className="h-5 w-5 text-muted-foreground" />
      </Button>

      {/* Delete Form */}
      <AlertDialog
        onOpenChange={(open) => {
          if (!open) setOpen(false)
        }}
      >
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => e.stopPropagation()}
          >
            <Trash2Icon className="h-5 w-5 text-red-500" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your data and remove your
              data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:space-x-4">
            <Button
              variant="destructive"
              isLoading={isPending}
              onClick={() => mutate(formId)}
            >
              Yes, delete form
            </Button>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
